import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  rewardsEngineService,
  RewardsEngine,
} from "@/services/rewards-engine.service";

const REWARDS_ENGINE_LIST_KEY = "get-rewards-engine";
const REWARDS_ENGINE_BY_ID_KEY = "get-rewards-engine-by-id";

export const useRewardsEngine = (
  enabled = false,
  filters?: { eventType?: string; event?: string }
) => {
  return useQuery<RewardsEngine[], Error>({
    queryKey: [REWARDS_ENGINE_LIST_KEY, filters],
    queryFn: () => rewardsEngineService.findAll(filters),
    staleTime: 1 * 60 * 60 * 1000,
    enabled,
  });
};

export const useRewardConfig = (id: string, enabled = true) => {
  return useQuery<RewardsEngine, Error>({
    queryKey: [REWARDS_ENGINE_BY_ID_KEY, id],
    queryFn: () => rewardsEngineService.findOne(id),
    enabled: !!id && enabled,
  });
};

export const useCreateRewardConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<RewardsEngine>) => {
      const response = await rewardsEngineService.create(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REWARDS_ENGINE_LIST_KEY] });
    },
  });
};

export const useUpdateRewardConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RewardsEngine> }) =>
      rewardsEngineService.update({ id, data }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [REWARDS_ENGINE_LIST_KEY] });
      queryClient.invalidateQueries({
        queryKey: [REWARDS_ENGINE_BY_ID_KEY, variables.id],
      });
    },
  });
};

export const useArchiveRewardConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rewardsEngineService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REWARDS_ENGINE_LIST_KEY] });
    },
  });
};
